import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DahboardTable from "./component/DahboardTable";
import { ApiHandler } from "../../../helper/ApiHandler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { SearchClear } from "../../../helper/CommonFunction";
import RevenueChart from "./component/RevenueChart";
import DataFetching from "../../../helper/DataFetching";
import SEO from "../../../utils/Seo";
import {
  FaEye,
  FaMousePointer,
  FaDollarSign,
  FaPercentage,
  FaBullseye,
  FaChartLine,
  FaRegEye,
  FaLayerGroup,
  FaSlidersH,
} from "react-icons/fa";

/* ================== SCOPED STYLES ================== */
const DASHBOARD_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600&family=Manrope:wght@400;500;600;700;800&display=swap');
  .dash-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .dash-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  .dash-body select.form-control,
  .dash-body input.form-control {
    border-radius: 0.75rem;
  border: 1px solid #F0DCC8;
  background-color: #ffffff;
  padding: 0.55rem 2rem 0.55rem 0.75rem; /* right side extra space for arrow */
  font-size: 0.875rem;
  color: #1C1410;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%234D3E30' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  background-size: 1rem;
  }
  .dash-body select.form-control:focus,
  .dash-body input.form-control:focus {
    outline: none;
    border-color: #D97706;
    box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.15);
  }
`;

/* ================== FORMATTER ================== */
const formatTwoDecimal = (value) => {
  const num = Number(value);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};

/* ================== INITIAL STATE ================== */
const insitalState = {
  total_impressions: 0,
  total_click: 0,
  revenue: 0,
  fill_rate: 0,
  ctr: 0,
  cpm: 0,
  viewability: 0,
  viewable_imps: 0,
  chart_data: null,
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userLoginToken } = useSelector((state) => state.user);

  const [site, setSite] = useState("All Sites");
  const [dateRange, setDateRange] = useState("Today");
  const [rangeData, setRangeData] = useState("Today");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [apiDataShow, setApiDataShow] = useState(insitalState);
  const [getDomainData, setGetDomainData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pending, setPending] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [resetSearch, setResetSearch] = useState(true);

  const {
    cpm,
    ctr,
    fill_rate,
    revenue,
    total_click,
    total_impressions,
    viewability,
    viewable_imps,
    chart_data,
  } = apiDataShow;

  /* ================== FETCH DOMAIN ================== */
  useEffect(() => {
    if (!userLoginToken) return;

    const fetchgetDomainData = async () => {
      try {
        const response = await ApiHandler(
          "/get_success_domain.php",
          "GET",
          undefined,
          userLoginToken,
          dispatch,
          navigate
        );

        if (response?.data?.status === "1") {
          setGetDomainData(response.data.data || []);
        }
      } catch {
        toast.error("Error fetching domain list");
      }
    };

    fetchgetDomainData();
  }, [userLoginToken, dispatch, navigate]);

  /* ================== DATE HELPERS ================== */
  const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  const getDateRange = () => {
    const currentDate = new Date();
    let start_date, end_date;

    switch (dateRange) {
      case "Today":
        start_date = end_date = formatDate(currentDate);
        break;

      case "Yesterday":
        currentDate.setDate(currentDate.getDate() - 1);
        start_date = end_date = formatDate(currentDate);
        break;

      case "Last 7 Days":
        start_date = formatDate(
          new Date(currentDate.setDate(currentDate.getDate() - 6))
        );
        end_date = formatDate(new Date());
        break;

      case "Last 30 Days":
        start_date = formatDate(
          new Date(currentDate.setDate(currentDate.getDate() - 29))
        );
        end_date = formatDate(new Date());
        break;

      case "Custom Date":
        if (!customDateRange[0] || !customDateRange[1]) {
          toast.error("Select valid date range");
          return null;
        }
        start_date = formatDate(customDateRange[0]);
        end_date = formatDate(customDateRange[1]);
        break;

      default:
        return null;
    }

    return { start_date, end_date };
  };

  /* ================== FETCH DASHBOARD DATA ================== */
  const getDashboardData = async () => {
    const dateRangeValues = getDateRange();
    if (!dateRangeValues) return;

    const { start_date, end_date } = dateRangeValues;
    const domain_id = site === "All Sites" ? "" : site;

    const commonBody = { domain_id, start_date, end_date };

    try {
      const response = await ApiHandler(
        "/get_revenue_dashboard.php",
        "POST",
        commonBody,
        userLoginToken,
        dispatch,
        navigate
      );

      if (response?.data?.status === "1") {
        setApiDataShow(response.data.data || insitalState);
        setRangeData(dateRange);
      } else {
        setApiDataShow(insitalState);
      }
    } catch {
      toast.error("Error fetching dashboard data");
    }

    const tableBody = {
      ...commonBody,
      page: currentPage - 1,
      perpage: pageLimit,
    };

    setPending(true);
    DataFetching(
      setPending,
      "/get_revenue_data.php",
      "POST",
      userLoginToken,
      dispatch,
      navigate,
      setFilteredData,
      setTotalPages,
      tableBody,
      setResetSearch,
      resetSearch
    );
  };

  useEffect(() => {
    if (resetSearch) getDashboardData();
  }, [currentPage, pageLimit, site, dateRange, customDateRange, resetSearch]);

  /* ================== HANDLERS ================== */
  const handleClearSearch = () => {
    setSite("All Sites");
    setDateRange("Today");
    setRangeData("Today");
    setCustomDateRange([null, null]);
    setCurrentPage(1);
    setResetSearch(true);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setResetSearch(true);
  };

  /* ================== STAT CARD META (display only) ================== */
  const statCards = [
    { title: "Impression", amount: total_impressions, icon: <FaEye /> },
    { title: "Click", amount: total_click, icon: <FaMousePointer /> },
    { title: "Revenue", amount: `$${formatTwoDecimal(revenue)}`, icon: <FaDollarSign /> },
    { title: "Fill Rate (%)", amount: `${formatTwoDecimal(fill_rate)}%`, icon: <FaPercentage /> },
    { title: "CTR (%)", amount: `${formatTwoDecimal(ctr)}%`, icon: <FaBullseye /> },
    { title: "CPM", amount: `$${formatTwoDecimal(cpm)}`, icon: <FaChartLine /> },
    { title: "Viewability (%)", amount: `${formatTwoDecimal(viewability)}%`, icon: <FaRegEye /> },
    { title: "Viewable Imps", amount: formatTwoDecimal(viewable_imps), icon: <FaLayerGroup /> },
  ];

  return (
    <>
      <SEO
        title="Dashboard - MyAd360 Publisher Control Panel"
        description="Track your ad performance and revenue metrics"
      />

      <style>{DASHBOARD_STYLES}</style>

      <Breadcrumb pageName="Dashboard" />

      <div className="dash-body min-h-screen">
        <div>
          <div
            className="bg-white p-4 border border-[#F0DCC8] shadow-[0_10px_30px_-18px_rgba(22,34,27,0.25)]"
            style={{ borderRadius: "1.5rem" }}
          >
            <div
              className="bg-gradient-to-br from-[#FFF4E5] to-[#FEF3E5] w-full p-4 border border-[#FDE8CD]"
              style={{ borderRadius: "1.2rem" }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span
                  className="w-8 h-8 shrink-0 flex items-center justify-center text-[#D97706] text-sm"
                  style={{
                    background: "linear-gradient(145deg, #FFFFFF, #FDE8CD)",
                    borderRadius: "58% 42% 55% 45% / 45% 55% 42% 58%",
                  }}
                >
                  <FaSlidersH />
                </span>
                <p className="dash-display text-base sm:text-lg font-semibold text-[#1C1410]">
                  Filters
                </p>
              </div>

              {/* FILTERS */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                <div>
                  <label className="text-sm font-medium text-[#4D3E30] mb-1 block">
                    Select App
                  </label>
                  <select
                    className="form-control"
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                  >
                    <option>All Sites</option>
                    {getDomainData.map((option, index) => (
                      <option key={index} value={option.id}>
                        {option.app_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium text-[#4D3E30] mb-1 block">
                    Date Range
                  </label>
                  <select
                    className="form-control"
                    value={dateRange}
                    onChange={(e) => {
                      setDateRange(e.target.value);
                      setCustomDateRange([null, null]);
                    }}
                  >
                    <option>Today</option>
                    <option>Yesterday</option>
                    <option>Last 7 Days</option>
                    <option>Last 30 Days</option>
                    <option>Custom Date</option>
                  </select>
                </div>

                {dateRange === "Custom Date" && (
                  <div className="relative">
                    <label className="text-sm font-medium text-[#4D3E30] mb-1 block">
                      Custom Date Range
                    </label>
                    <DatePicker
                      selected={customDateRange[0]}
                      onChange={(update) => setCustomDateRange(update)}
                      startDate={customDateRange[0]}
                      endDate={customDateRange[1]}
                      selectsRange
                      className="form-control required"
                      placeholderText="Select Date Range"
                      dateFormat="yyyy-MM-dd"
                      required
                    />
                  </div>
                )}
              </div>

              <div className="mt-4">
                <SearchClear search={handleSearch} clear={handleClearSearch} />
              </div>
            </div>

            {/* ================== STAT CARDS ================== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
              {statCards.map((stat, idx) => (
                <div
                  key={idx}
                  className="relative bg-white border border-[#F0DCC8] shadow-sm hover:shadow-[0_16px_36px_-18px_rgba(217,119,6,0.35)] transition-shadow duration-300 p-4 overflow-hidden"
                  style={{ borderRadius: "1.2rem 0.5rem 1.2rem 0.5rem" }}
                >
                  <div
                    className="absolute top-0 left-6 right-6 h-[3px] rounded-b-full"
                    style={{ background: idx % 2 === 0 ? "#D97706" : "#EA580C" }}
                  />
                  <div
                    className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[#D97706] text-sm sm:text-base mb-3"
                    style={{
                      background: "linear-gradient(145deg, #FFF4E5, #FDE8CD)",
                      borderRadius: "58% 42% 55% 45% / 45% 55% 42% 58%",
                    }}
                  >
                    {stat.icon}
                  </div>
                  <p className="text-[#8A7560] font-semibold text-xs sm:text-sm">
                    {stat.title}
                  </p>
                  <p className="text-[#1C1410] text-lg sm:text-xl font-extrabold mt-0.5">
                    {stat.amount}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {chart_data && (
            <RevenueChart revenueData={chart_data} dateRange={rangeData} />
          )}

          {filteredData && (
            <DahboardTable
                filteredData={filteredData}
                pending={pending}
                currentPage={currentPage}
                setPageLimit={setPageLimit}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                pageLimit={pageLimit}
                setResetSearch={setResetSearch}
              />
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;